import { useState } from 'react'
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { isEqual, pick } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import { SubmitButton } from 'components/Buttons'
import { TITLE_OPTIONS } from 'constants'
import { PHONE_REGEXP, sequencePhones } from 'utils/phone'
import PhoneList from 'pages/Member/Contact/Form/PhoneList'
import { save } from 'store/academic/parent/contact-change'
import * as snackbar from 'utils/snackbar'

const toPhones = phones =>
  sequencePhones((phones ?? []).filter(phone => phone?.number).map(phone => pick(phone, ['id', 'type', 'number'])))

export default function ContactChangeForm({ memberId, data, isPrimary, onClose, onSaved }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [passwordVisibility, setPasswordVisibility] = useState(false)

  const relationshipTypes = useSelector(state => state.lookup.relationshipTypes)
  const languages = useSelector(state => state.lookup.languages)

  const contact = data?.contact ?? {}
  const address = contact.addresses?.[0] ?? {}
  const notification = data?.notification ?? []
  const titleOptions = contact.title && !TITLE_OPTIONS.includes(contact.title) ? [...TITLE_OPTIONS, contact.title] : TITLE_OPTIONS

  const schema = yup.object().shape({
    title: yup.string().nullable().max(32),
    firstName: yup.string().required(t('Required')).max(48),
    lastName: yup.string().required(t('Required')).max(48),
    email: contact.email
      ? yup.string().email(t('Enter a valid email')).required(t('Required')).max(64)
      : yup.string().email(t('Enter a valid email')).nullable().max(64),
    password: yup.string().max(128),
    phones: yup.array().of(
      yup.object().shape({
        number: yup.string().matches(PHONE_REGEXP, { message: t('Enter a valid phone number'), excludeEmptyString: true }),
        type: yup.string().when('number', {
          is: number => number && number.length > 0,
          then: yup.string().required(t('Type is required')),
          otherwise: yup.string(),
        }),
      }),
    ),
  })

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: contact.title ?? null,
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      email: contact.email ?? '',
      preferredLanguage: contact.preferredLanguage ?? '',
      phones: contact.phones?.length ? toPhones(contact.phones) : [{ type: 'home', number: '' }],
      password: '',
    },
  })

  const phoneArrayControl = useFieldArray({ control, name: 'phones', keyName: 'uuid' })

  const onSubmit = async formData => {
    const payload = {}
    const title = formData.title || null
    const email = formData.email?.trim() || null
    const preferredLanguage = formData.preferredLanguage || null
    const phones = toPhones(formData.phones)

    if (title !== (contact.title ?? null)) payload.title = title
    if (formData.firstName !== contact.firstName) payload.firstName = formData.firstName
    if (formData.lastName !== contact.lastName) payload.lastName = formData.lastName
    if (email && email !== contact.email) payload.email = email
    if (preferredLanguage !== (contact.preferredLanguage ?? null)) payload.preferredLanguage = preferredLanguage
    if (!isEqual(phones, toPhones(contact.phones))) payload.phones = phones
    if (formData.password) payload.password = formData.password

    if (!Object.keys(payload).length) return onClose()

    setSaving(true)
    const res = await dispatch(save({ memberId, ...payload }))
    setSaving(false)
    if (!res.error) {
      snackbar.success(t('Your contact information has been updated'))
      onSaved()
    }
  }

  const textField = (name, label, props = {}) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          size="small"
          fullWidth
          label={label}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          {...props}
          {...field}
          value={field.value ?? ''}
        />
      )}
    />
  )

  const readOnlyField = (label, value) => <TextField size="small" fullWidth disabled label={label} value={value ?? ''} />

  if (!data?.contact) {
    return (
      <Box sx={{ py: 2 }}>
        <Header title={t('Parent/Guardian Information')} small close={onClose} borderBottom />
        <Alert severity="error" sx={{ m: 3 }}>
          {t('Your contact information could not be loaded. Close this panel and try again.')}
        </Alert>
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ py: 2, overflowY: 'auto' }}>
      <Header title={t('Parent/Guardian Information')} small close={onClose} borderBottom />

      <Box sx={{ px: 3, pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {readOnlyField(t('Relationship'), relationshipTypes.find(item => item.id === data.relationship)?.name ?? '')}
          </Grid>

          <Grid item xs={6}>
            {textField('title', t('Title'), {
              select: true,
              children: titleOptions.map(title => (
                <MenuItem key={title} value={title}>
                  {title}
                </MenuItem>
              )),
            })}
          </Grid>

          <Grid item xs={6}>
            {textField('firstName', t('First Name'))}
          </Grid>
          <Grid item xs={6}>
            {textField('lastName', t('Last Name'))}
          </Grid>

          <Grid item xs={6}>
            {textField('email', t('Email'))}
          </Grid>
          <Grid item xs={6}>
            {textField('preferredLanguage', t('Preferred Language'), {
              select: true,
              children: [
                <MenuItem key="" value="">
                  <em>{t('None')}</em>
                </MenuItem>,
                ...(languages ?? []).map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} - {item.originName}
                  </MenuItem>
                )),
              ],
            })}
          </Grid>
          <Grid item xs={6}>
            {readOnlyField(t('Username'), data?.contact?.user?.username ?? '')}
          </Grid>
          <Grid item xs={6}>
            {textField('password', t('New Password'), {
              type: passwordVisibility ? 'text' : 'password',
              autoComplete: 'new-password',
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setPasswordVisibility(!passwordVisibility)}
                      edge="end"
                    >
                      {passwordVisibility ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            })}
          </Grid>

          <Grid item xs={6}>
            {readOnlyField(t('Street Address'), address.street)}
          </Grid>
          <Grid item xs={6}>
            {readOnlyField(t('Street Address 2'), address.line2)}
          </Grid>
          <Grid item xs={6}>
            {readOnlyField(t('City'), address.city)}
          </Grid>
          <Grid item xs={3}>
            {readOnlyField(t('State'), address.state)}
          </Grid>
          <Grid item xs={3}>
            {readOnlyField(t('Postal Code'), address.zip)}
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              {isPrimary
                ? t('To change your address, use Request Address Change at the top of the Contacts page.')
                : t('Only the primary parent/guardian can request an address change.')}
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <PhoneList control={control} path="phones" minCount={2} {...phoneArrayControl} />
          </Grid>


          <Grid item xs={12}>
            <FormControl fullWidth sx={{ p: 1, pl: 2, borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography color="text.disabled">{t('Notifications')}</Typography>
              <Stack flexDirection="column" sx={{ ml: 2 }}>
                {[
                  { label: t('Phone'), checked: notification.includes('phone') },
                  { label: t('Email'), checked: notification.includes('email') },
                  { label: t('Text'), checked: notification.includes('sms') },
                  { label: t('Allow Pickup'), checked: data.pickup },
                  { label: t('Emergency Call'), checked: data.emergency },
                  { label: t('SIS Access'), checked: data.hasParentalAccess },
                ].map(item => (
                  <FormControlLabel
                    key={item.label}
                    disabled
                    control={<Checkbox color="primary" checked={!!item.checked} />}
                    label={item.label}
                  />
                ))}
              </Stack>
            </FormControl>
          </Grid>

        </Grid>

        <Stack justifyContent="flex-end" flexDirection="row" sx={{ my: 4 }}>
          <SubmitButton size="small" color="primary" title={t('SAVE')} disabled={saving} onClick={handleSubmit(onSubmit)} />
        </Stack>
      </Box>
    </Box>
  )
}
